import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import Modal from './Modal';
import { useI18n } from '../../utils/i18n';

// Kanonischer Bestätigungsdialog (Frontend Phase 2, #31 / F5).
// Ersetzt die window.confirm-Aufrufe im Bestand. Zwei Nutzungsarten:
//
// 1) Promise-basiert über den Provider (bevorzugt, minimale Call-Site-Änderung):
//      const confirm = useConfirm();
//      if (!(await confirm('Wirklich löschen?'))) return;
//    confirm() akzeptiert einen String (message) oder
//    { title, message, confirmLabel, cancelLabel, danger }.
//
// 2) Deklarativ als <ConfirmDialog open … onConfirm onCancel /> für Fälle mit
//    eigenem State (z. B. bestehende Modal-basierte Bestätigungen).
//
// Der Provider hängt in App.js innerhalb des I18nProviders.

const ConfirmContext = createContext(null);

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel
}) => {
  const { t } = useI18n();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title || t('confirm.title', 'Bestätigung')}
      maxWidth="440px"
      closeDisabled={busy}
      closeLabel={t('common.close', 'Schließen')}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel || t('common.cancel', 'Abbrechen')}
          </button>
          <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm} disabled={busy}>
            {confirmLabel || t('common.ok', 'OK')}
          </button>
        </>
      )}
    >
      <p className="ui-confirm-message">{message}</p>
    </Modal>
  );
};

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    const options = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => {
      // Ein evtl. noch offener Dialog wird als "abgebrochen" aufgelöst
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setDialog(options);
    });
  }, []);

  const settle = (result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    if (resolve) resolve(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!dialog}
        title={dialog?.title}
        message={dialog?.message}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.cancelLabel}
        danger={!!dialog?.danger}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmContext.Provider>
  );
};

// Fallback außerhalb des Providers: natives window.confirm, damit nichts bricht
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  return confirm || ((opts) => {
    const message = typeof opts === 'string' ? opts : (opts?.message || '');
    return Promise.resolve(window.confirm(message));
  });
};

export default ConfirmDialog;
